export type EventData = Record<string, string | undefined>;
import { useEffect, useMemo } from 'react';
import { usePageData } from 'lib/page-data/PageDataContext';
import {
  getAssetClassName,
  getSelectedShareClass,
  getSubAssetClassName,
} from '@/store/fundDetails';
import { useSelector } from 'react-redux';
import { useSitecoreContext } from './useSitecoreContext';
import { BreadcrumbsProps } from 'model/Breadcrumbs';
import findComponentInTree from 'lib/utils/findComponentInTree';
import { fundDetailsDataFetcher } from 'lib/wildcards/dataFetchers/FundDetailsDataFetcher';

type PropositionType = {
  investment_fund: {
    fund_code: string;
  };
};

type ProductTags = {
  assetClasses: string | Array<string>;
  subAssetClasses: string | Array<string>;
  capabilities?: Array<string>;
};

type CustomDataType = {
  event: string;
  page_title: string | undefined;
  page_location: string | undefined;
  breadcrumbs: Array<string> | string;
  proposition: PropositionType | undefined;
  product_tags: ProductTags | undefined;
  global: any;
};

const replaceNullValues = (data: CustomDataType) => {
  const replaceNull = (value) => {
    if (value === null || value === '') {
      return 'n/a';
    } else if (Array.isArray(value)) {
      return value.map(replaceNull);
    } else if (typeof value === 'object' && value !== null) {
      const newObj = {};
      for (const key in value) {
        newObj[key] = replaceNull(value[key]);
      }
      return newObj;
    } else {
      return value;
    }
  };

  return replaceNull(data);
};

export const pushDataLayerEvent = (data: EventData): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }
};

export const pushDataLayerEventAfterOneTrust = (data: EventData): void => {
  if (window.OneTrust?.IsAlertBoxClosed()) {
    // Case 1: OneTrust finished initialization and consent is already granted (earlier).
    pushDataLayerEvent(data);
  } else if (window.OneTrust) {
    // Case 2: OneTrust is loaded, but consent has not been granted yet, so wait for it.
    window.OneTrust.OnConsentChanged(() => {
      window.OneTrust?.OnConsentChanged(() => null);
      pushDataLayerEvent(data);
    });
  } else {
    // Case 3: OneTrust has not been initialized yet, wait for it.
    window.OptanonWrapper = () => {
      window.OptanonWrapper = undefined;
      pushDataLayerEventAfterOneTrust(data);
    };
  }
};

export const useDataLayerUpdate = () => {
  const { sitecoreContext } = useSitecoreContext();
  const pageTitle: string | undefined = usePageData('pageTitle');
  const breadcrumbComponent = findComponentInTree<BreadcrumbsProps['fields']>(
    sitecoreContext.route?.placeholders['abrdn-header'],
    'Breadcrumbs'
  );
  const breadcrumbs =
    breadcrumbComponent?.fields?.breadcrumbs.value.map(({ name }) => name) || 'n/a';
  const selectedShareClass = useSelector(getSelectedShareClass);
  const assetClassName: string = useSelector(getAssetClassName);
  const subAssetClassName: string = useSelector(getSubAssetClassName);
  const language: string = sitecoreContext?.language?.split('-')[0];
  const country: string = sitecoreContext?.language?.split('-')[1];
  const sitecoreDataLayerData: any = { ...sitecoreContext.dataLayer };

  const customData: CustomDataType = useMemo(() => {
    return {
      event: 'page_view',
      page_title: sitecoreContext.metaTitle || pageTitle,
      breadcrumbs: breadcrumbs,
      page_location: (typeof window !== 'undefined' && window.location.href) || '',
      global: {
        ...sitecoreDataLayerData.global,
        country,
        language,
      },
      proposition: {
        investment_fund: {
          fund_code: selectedShareClass?.isin || 'n/a',
        },
      },
      product_tags: selectedShareClass
        ? {
            assetClasses: assetClassName,
            subAssetClasses: subAssetClassName,
          }
        : {
            assetClasses: sitecoreContext.route.fields?.pageFundAssetClasses
              ?.map((item) => item.fields.className.value)
              .join('|'),
            subAssetClasses: sitecoreContext.route.fields?.pageFundSubAssetClasses
              ?.map((item) => item.fields.name.value)
              .join('|'),
            capabilities: sitecoreContext.route.fields?.pageFundCapabilities
              ?.map((item) => item.fields.name.value)
              .join('|'),
          },
    };
  }, [sitecoreContext, breadcrumbs, assetClassName, selectedShareClass]);

  useEffect(() => {
    const templateId = sitecoreContext.route?.templateId;
    if (!fundDetailsDataFetcher.templateIds.includes(templateId)) {
      // handle all the pages except fundDetails
      pushDataLayerEventAfterOneTrust(
        replaceNullValues(Object.assign(sitecoreDataLayerData, customData))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitecoreContext]);

  useEffect(() => {
    if (selectedShareClass) {
      // handle fundDetails page data specifically
      pushDataLayerEventAfterOneTrust(
        replaceNullValues(Object.assign(sitecoreDataLayerData, customData))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShareClass]);
};
